import { TransactionResponse } from '@ethersproject/abstract-provider';
import { detectManifoldEthereumProvider } from '@manifoldxyz/frontend-provider-utils';
import { URLs } from '@manifoldxyz/js-ts-utils';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { reservoirAPI } from '@/api/reservoir';
import { DEFAULT_STEP_STATE } from '@/common/constants';
import networkCheck from '@/common/networkCheck';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import {
  StepConfig,
  TransactionStep,
  TransactionStepState,
  TxStepId,
  TxStepStatus,
} from '@/common/types';
import { getTransactionErrorMessage } from '@/common/web3TransactionErrorHandling';
import useMainStore, { StoreType } from '@/store/mainStore';

export function createBuyStep(
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
  overrides?: {
    config?: Partial<StepConfig>;
    state?: Partial<TransactionStepState>;
  },
): TransactionStep {
  return {
    state: {
      ...DEFAULT_STEP_STATE,
      descriptionByStatus: {
        [TxStepStatus.Unstarted]: 'Purchases the token from the secondary market',
        [TxStepStatus.Pending]: 'Confirm the purchase transaction in your wallet...',
        [TxStepStatus.Confirming]: 'Waiting for blockchain confirmation of purchase...',
        [TxStepStatus.Confirmed]: 'Purchase complete',
        [TxStepStatus.Failed]: 'Purchase failed',
      },
      ...overrides?.state,
    },
    config: {
      id: TxStepId.Buy,
      name: 'Buy',
      ...overrides?.config,
    },
    execute: async (
      _: StoreType,
      updateStepState: (updates: Partial<TransactionStepState>) => void,
    ) => {
      const walletAddress = useMainStore.getState().walletAddress;
      const reservoirFloorToken = useMainStore.getState().reservoirFloorToken;
      const analytics = useMainStore.getState().analytics;

      // Track the beginning of secondary purchase transaction
      analytics?.capture({
        event: TELEMETRY_EVENT_MAP.SECONDARY_PURCHASE_TX_ATTEMPT,
      });

      if (!walletAddress) {
        throw new Error('Wallet address for purchase not found');
      }

      if (!reservoirFloorToken) {
        throw new Error('Listing expired, please reload page and try again');
      }

      // Get the mintTo address from the store if available, otherwise use walletAddress
      // mintTo allows for delegate wallet and gifting support
      const numberOfMints = useMainStore.getState().numberOfMints;

      await networkCheck(studioInstance.publicData.network);

      const ethersProvider = (
        await detectManifoldEthereumProvider({ initialized: true })
      )?.provider();
      const signer = await ethersProvider?.getSigner();

      if (!signer) {
        throw new Error('Signer is not valid');
      }

      let claimTx: TransactionResponse | undefined;
      try {
        const txData = await reservoirAPI.getBuyTransactionData(
          studioInstance.publicData.contract.contractAddress,
          studioInstance.publicData.network,
          reservoirFloorToken.token.tokenId,
          walletAddress,
          numberOfMints,
        );

        if (!txData) {
          throw new Error('Failed to get transaction data');
        }

        // mark that the tx step is Confirming
        updateStepState({
          status: TxStepStatus.Confirming,
        });

        claimTx = await signer?.sendTransaction(txData);
      } catch (error) {
        // Track failed transaction
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.REDEEM_TX_FAIL,
          properties: {
            user_wallet: walletAddress?.toLowerCase(),
          },
        });
        console.warn('error', error);
        throw new Error(getTransactionErrorMessage(error));
      }

      // mark that buy tx step as Confirming
      updateStepState({
        status: TxStepStatus.Confirming,
      });

      if (claimTx) {
        try {
          try {
            const receipt = await claimTx.wait(1);
            useMainStore.getState().setMintedTxHash(receipt.transactionHash);
            useMainStore.getState().setMintedTokenIds([reservoirFloorToken.token.tokenId]);

            // Track successful transaction
            analytics?.capture({
              event: TELEMETRY_EVENT_MAP.SECONDARY_PURCHASE_TX_SUCCESS,
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            // Handle speed-up case which is a false error in a sense
            if (
              error?.code === 'TRANSACTION_REPLACED' &&
              !error.cancelled &&
              error.replacement?.hash
            ) {
              updateStepState({
                etherscanLink: URLs.getEtherscanURLForTransactionHash(
                  studioInstance.publicData.network,
                  error.replacement.hash,
                ),
                txHash: error.replacement.hash,
              });
              // Wait for the replacement transaction
              const provider = window.ManifoldEthereumProvider.provider();
              if (!provider) throw new Error('No provider available');
              const replacementTx = await provider.getTransaction(error.replacement.hash);
              const replacementReceipt = await replacementTx.wait(1);
              useMainStore.getState().setMintedTxHash(replacementReceipt.transactionHash);
              useMainStore.getState().setMintedTokenIds([reservoirFloorToken.token.tokenId]);

              // Track successful transaction after speed-up
              analytics?.capture({
                event: TELEMETRY_EVENT_MAP.SECONDARY_PURCHASE_TX_SUCCESS,
              });
            } else {
              // Track failed transaction
              analytics?.capture({
                event: TELEMETRY_EVENT_MAP.SECONDARY_PURCHASE_TX_FAIL,
              });
              // otherwise just rethrow the error for getTransactionErrorMessage to handle
              throw error;
            }
          }
        } catch (error) {
          // Track failed transaction
          analytics?.capture({
            event: TELEMETRY_EVENT_MAP.SECONDARY_PURCHASE_TX_FAIL,
          });
          throw new Error(getTransactionErrorMessage(error));
        }
      }
    },
  };
}
